import { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import { products, productCategories } from "@/mocks/products";
import { useSeo, SITE_LOGO, getCanonicalUrl } from "@/lib/seo";
import { useJsonLd } from "@/lib/schema";

const productMeta: Record<string, { title: string; description: string; keywords: string }> = {
  en: {
    title: "All Products - CSSBuy Spreadsheet",
    description: "Browse the full catalog of products available through CSSBuy Spreadsheet.",
    keywords: "CSSBuy Spreadsheet, CSSBuy products, CSSBuy catalog",
  },
  pt: {
    title: "Todos os Produtos - CSSBuy Spreadsheet",
    description: "Navegue pelo catálogo completo de produtos disponíveis através do CSSBuy Spreadsheet.",
    keywords: "CSSBuy Spreadsheet, produtos CSSBuy, catálogo CSSBuy",
  },
  de: {
    title: "Alle Produkte - CSSBuy Spreadsheet",
    description: "Durchsuche den vollständigen Produktkatalog, der über CSSBuy Spreadsheet verfügbar ist.",
    keywords: "CSSBuy Spreadsheet, CSSBuy Produkte, CSSBuy Katalog",
  },
};

export default function ProductsPage() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [activeCategory, setActiveCategory] = useState("All");

  const currentLang = i18n.language;

  const seoData = useMemo(() => {
    const meta = productMeta[currentLang] || productMeta.en;
    return {
      title: meta.title,
      description: meta.description,
      keywords: meta.keywords,
      image: SITE_LOGO,
      type: "website",
    };
  }, [currentLang]);

  useSeo(seoData);

  useJsonLd("products-collection", {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: seoData.title,
    description: seoData.description,
    url: getCanonicalUrl(location.pathname),
    inLanguage: currentLang,
  });

  useJsonLd("products-list", {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: seoData.title,
    itemListElement: products.map((p, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: p.name,
      url: p.link,
    })),
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    const pathLang = location.pathname.split("/")[1];
    if (["en", "pt", "de"].includes(pathLang) && i18n.language !== pathLang) {
      i18n.changeLanguage(pathLang);
    }
  }, [location.pathname, i18n]);

  const filteredProducts = useMemo(() => {
    if (activeCategory === "All") return products;
    return products.filter((p) => p.category === activeCategory);
  }, [activeCategory]);

  const getHomePath = () => `/${currentLang}`;

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      <main className="flex-1">
        {/* Page Hero */}
        <section className="py-12 md:py-16 bg-emerald-50/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav className="text-sm text-gray-500 mb-6">
              <Link to={getHomePath()} className="hover:text-emerald-700 transition-colors">
                {t("nav.home")}
              </Link>
              <span className="mx-2">/</span>
              <span className="text-gray-900 font-medium">{t("products.title")}</span>
            </nav>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
              {t("products.title")}
            </h1>
            <p className="text-gray-600 text-lg max-w-2xl">
              {t("products.subtitle")}
            </p>
          </div>
        </section>

        {/* Category Filter */}
        <section className="py-6 border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-2 mb-3">
              <i className="ri-filter-3-line text-emerald-700" />
              <span className="text-sm font-semibold text-gray-700">
                {t("products.filterBy")}
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setActiveCategory("All")}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                  activeCategory === "All"
                    ? "bg-emerald-700 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {t("products.allCategories")}
                <span className="ml-1.5 text-xs opacity-80">({products.length})</span>
              </button>
              {productCategories.map((cat) => {
                const count = products.filter((p) => p.category === cat).length;
                return (
                  <button
                    key={cat}
                    onClick={() => setActiveCategory(cat)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                      activeCategory === cat
                        ? "bg-emerald-700 text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    {cat}
                    <span className="ml-1.5 text-xs opacity-80">({count})</span>
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        {/* Product Grid */}
        <section className="py-10 md:py-14">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-6">
              <p className="text-sm text-gray-500">
                {t("products.showing")} <span className="font-semibold text-gray-900">{filteredProducts.length}</span> {t("products.products")}
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-6">
              {filteredProducts.map((product) => (
                <a
                  key={product.id}
                  href={product.link}
                  target="_blank"
                  rel="noopener nofollow"
                  className="group block bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow duration-300"
                  data-product-shop
                >
                  <div className="aspect-square overflow-hidden bg-gray-100 relative">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors duration-300" />
                  </div>
                  <div className="p-3 md:p-4">
                    <p className="text-xs font-medium text-emerald-700 mb-1">
                      {product.category}
                    </p>
                    <h3 className="text-sm font-medium text-gray-900 leading-snug line-clamp-2 mb-2 min-h-[2.5rem]">
                      {product.name}
                    </h3>
                    <p className="text-sm font-bold text-emerald-700 mb-3">
                      {product.price}
                    </p>
                    <span className="inline-flex items-center justify-center w-full px-3 py-2 bg-emerald-700 text-white text-sm font-medium rounded-lg hover:bg-emerald-800 transition-colors">
                      <i className="ri-shopping-bag-line mr-1.5" />
                      {t("products.buyNow")}
                    </span>
                  </div>
                </a>
              ))}
            </div>

            {filteredProducts.length === 0 && (
              <div className="text-center py-20">
                <i className="ri-inbox-line text-4xl text-gray-300 mb-3" />
                <p className="text-gray-500">{t("products.noProducts")}</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}